// One line per row this character can see. A row carries the price of the item and nothing
// about what buying it would do to the character; the pinned strip is where that shows.

import { dollars_label, type PanelSpell } from "../vendor/web";
import {
  charged,
  discounted,
  pounds,
  poundsLabel,
  price,
  qualityOf,
  quantityOf,
  spellOf,
  type CartLine,
} from "./cart";
import type { BakedRow } from "./catalogue";
import { drReading, type BodyLocation, type DrReading } from "./coverage";
import { itemDetail } from "./detail";
import { qualifiedName, rowFor, type QualityTable, type Stall } from "./quality";
import { openSheet } from "./sheet";
import { choiceLabel, choices, placeholder, resolvedName } from "./spells";
import { bestUsage, usageLines, usageText } from "./usages";

export interface ShopActions {
  add(item: string): void;
  remove(item: string): void;
  setQuantity(item: string, quantity: number): void;
  setQuality(item: string, quality: string | undefined): void;
  setSpell(item: string, spell: string): void;
}

/** What a row whose description carries an unresolved placeholder needs to draw its
 *  select: the spells this character knows, the pages already written, the spell each page
 *  row names before it is bought, and the campaign rule the curation states beside the
 *  gate. */
export interface SpellPages {
  spells: readonly PanelSpell[];
  held: Map<string, number>;
  chosen: ReadonlyMap<string, string>;
  advisory: string;
}

/** Everything the rows draw from, whichever tab is drawing them. */
export interface ShopSurface {
  lines: CartLine[];
  actions: ShopActions;
  table?: QualityTable;
  pages?: SpellPages;
  /** Catalogue ids the character arrived with, which are charged for nothing. */
  owned?: ReadonlySet<string>;
  /** How many of a row one purchase is, for the rows the curation states a count on. */
  counts?: ReadonlyMap<string, number>;
  /** The tier each row not yet in the cart is selected at. Add is the only control that
   *  buys, so a tier tapped before then has nowhere else to live. */
  tiers?: ReadonlyMap<string, string>;
  /** This character's hit locations, which an armour row states its coverage in. */
  locations?: readonly BodyLocation[];
}

export function shopList(stalls: Stall[], surface: ShopSurface): HTMLElement {
  const list = document.createElement("ul");
  list.className = "shop";
  for (const stall of stalls) {
    list.append(shopRow(stall, surface));
  }
  return list;
}

function shopRow(stall: Stall, surface: ShopSurface): HTMLElement {
  const { lines, actions, table, pages, owned } = surface;
  const purchase = surface.counts?.get(stall.item) ?? 1;
  const quantity = quantityOf(lines, stall.item);
  const quality = qualityOf(lines, stall.item) ?? surface.tiers?.get(stall.item);
  const chosen = rowFor(stall, quality);
  const spell = spellOf(lines, stall.item) ?? pages?.chosen.get(stall.item);
  const page = placeholder(chosen) !== null;

  const item = document.createElement("li");
  item.className = quantity > 0 ? "shop-row shop-row-carted" : "shop-row";
  item.dataset["item"] = stall.item;
  item.dataset["quality"] = quality ?? "";
  if (page) item.dataset["spell"] = spell ?? "";

  const name = document.createElement("span");
  name.className = "shop-name";
  name.textContent = page ? resolvedName(chosen, spell) : qualifiedName(chosen, table);

  const held = quantity > 0 ? quantity : purchase;
  const arrived = owned?.has(stall.item) ?? false;
  const cost = document.createElement("span");
  cost.className = "shop-price";
  cost.textContent = arrived ? "owned" : dollars_label(charged(chosen, held));

  const prices = document.createElement("div");
  prices.className = "shop-prices";
  if (!arrived && discounted(chosen)) {
    const stock = document.createElement("s");
    stock.className = "shop-stock-price";
    stock.textContent = dollars_label(price(chosen, held));
    prices.append(stock);
  }
  prices.append(cost);

  const load = document.createElement("span");
  load.className = "shop-weight";
  load.textContent = poundsLabel(pounds(chosen, held));

  const controls = document.createElement("div");
  controls.className = "shop-controls";
  controls.append(infoButton(name.textContent ?? "", chosen));
  if (quantity > 0 && page) {
    controls.append(control("shop-remove", "Remove", () => actions.remove(stall.item)));
  } else if (page) {
    // A spell already carrying a page takes no second one, so a row naming that spell
    // stays refused.
    const free = spell !== undefined && !(pages?.held.has(spell) ?? false);
    controls.append(
      free ? control("shop-add", "Add", () => actions.add(stall.item)) : refused(),
    );
  } else if (quantity === 0) {
    controls.append(control("shop-add", "Add", () => actions.add(stall.item)));
  } else {
    controls.append(
      control("shop-fewer", "-", () => actions.setQuantity(stall.item, quantity - purchase)),
      count(quantity),
      control("shop-more", "+", () => actions.setQuantity(stall.item, quantity + purchase)),
      control("shop-remove", "Remove", () => actions.remove(stall.item)),
    );
  }

  item.append(name, prices, load);
  const stats = statLines(chosen, surface.locations ?? []);
  if (stats !== null) item.append(stats);
  item.append(controls);
  if (page && pages !== undefined) {
    item.append(spellPicker(stall.item, chosen, spell, pages, actions));
  } else if (stall.tiers.length > 0) {
    item.append(tierPicker(stall, quality, actions));
  }
  const caption = stall.tiers.find((tier) => tier.tier === quality)?.caption;
  if (caption) item.append(note("shop-caption", caption));
  return item;
}

/**
 * What the item does, resolved against this character and independent of the cart. One
 * usage stands on the row, the one this character rolls best with, and the rest are named
 * as a count so the panel behind (i) is worth opening.
 */
function statLines(row: BakedRow, locations: readonly BodyLocation[]): HTMLElement | null {
  const usages = usageLines(row);
  const best = bestUsage(usages);
  const armour = drReading(row, locations);
  if (best === null && armour === null) return null;

  const wrapper = document.createElement("div");
  wrapper.className = "shop-stats";
  if (best !== null) {
    const line = document.createElement("p");
    line.className = best.level === null ? "shop-usage shop-usage-unskilled" : "shop-usage";
    line.dataset["usage"] = best.name;
    line.textContent = usageText(best);
    wrapper.append(line);
  }
  const rest = usages.length - 1;
  if (rest > 0) {
    const more = document.createElement("p");
    more.className = "shop-more-usages";
    more.textContent = `${rest} more usage${rest === 1 ? "" : "s"} under (i)`;
    wrapper.append(more);
  }
  if (armour !== null) wrapper.append(drLine(armour));
  return wrapper;
}

function drLine(reading: DrReading): HTMLElement {
  const line = document.createElement("p");
  line.className = "shop-dr";
  const parts = reading.lines.map(
    (each) => `DR ${each.amount} ${each.covers.map((where) => where.name).join("/")}`,
  );
  for (const caveat of reading.caveats) {
    parts.push(`${signed(caveat.amount)} vs ${caveat.against}`);
  }
  line.textContent = parts.join(", ");
  return line;
}

function signed(amount: number): string {
  return amount > 0 ? `+${amount}` : String(amount);
}

/** What the library says the item is, for a player who cannot tell a bardiche from a bill. */
function infoButton(name: string, row: BakedRow): HTMLElement {
  const button = document.createElement("button");
  button.className = "shop-info";
  button.setAttribute("aria-label", `About ${name}`);
  button.textContent = "i";
  button.addEventListener("click", () => openSheet(name, itemDetail(row)));
  return button;
}

/** Stock, or one of the tiers this item's class offers. It is exactly one of these. */
function tierPicker(
  stall: Stall,
  quality: string | undefined,
  actions: ShopActions,
): HTMLElement {
  const picker = document.createElement("div");
  picker.className = "shop-quality";
  const option = (tier: string | undefined, label: string): HTMLElement => {
    const button = document.createElement("button");
    button.className = tier === quality ? "shop-tier shop-tier-chosen" : "shop-tier";
    button.dataset["tier"] = tier ?? "";
    button.textContent = label;
    button.addEventListener("click", () => actions.setQuality(stall.item, tier));
    return button;
  };
  picker.append(option(undefined, "Stock"));
  for (const tier of stall.tiers) picker.append(option(tier.tier, tier.label));
  return picker;
}

/**
 * The spell written on the page: required, single-select, and drawn from the spells this
 * character knows. A spell that already carries a page is offered and disabled with the
 * tier it has, because duplicates stack in GCS and two cheap pages would buy what one dear
 * one does.
 */
function spellPicker(
  item: string,
  row: Stall["stock"],
  spell: string | undefined,
  pages: SpellPages,
  actions: ShopActions,
): HTMLElement {
  const held = new Map(pages.held);
  if (spell !== undefined) held.delete(spell);
  const offered = choices(pages.spells, row, held);

  const picker = document.createElement("div");
  picker.className = "shop-spell";

  const select = document.createElement("select");
  select.className = "shop-spell-select";
  select.setAttribute("aria-label", "The spell written on this page");

  const blank = document.createElement("option");
  blank.className = "shop-spell-blank";
  blank.value = "";
  blank.textContent = "Choose a spell";
  select.append(blank);

  for (const choice of offered) {
    const taken = choice.held !== null;
    const option = document.createElement("option");
    option.className = taken ? "shop-spell-option shop-spell-taken" : "shop-spell-option";
    option.value = choice.name;
    option.textContent = choiceLabel(choice);
    option.disabled = taken;
    select.append(option);
  }
  select.value = spell ?? "";
  select.addEventListener("change", () => {
    if (select.value !== "") actions.setSpell(item, select.value);
  });
  picker.append(select);

  const moved = offered.find((choice) => choice.name === spell && choice.held === null);
  picker.append(
    moved === undefined
      ? note("shop-spell-gate", "Choose a spell: a page naming none costs the same and does nothing.")
      : note("shop-spell-moved", `${moved.name} ${moved.before} -> ${moved.after}`),
  );
  if (pages.advisory !== "") picker.append(note("shop-advisory", pages.advisory));
  return picker;
}

function note(className: string, text: string): HTMLElement {
  const line = document.createElement("p");
  line.className = className;
  line.textContent = text;
  return line;
}

function control(className: string, label: string, onClick: () => void): HTMLElement {
  const button = document.createElement("button");
  button.className = className;
  button.textContent = label;
  button.addEventListener("click", onClick);
  return button;
}

/** Add, offered and refused, because the select above it names no spell this page can take. */
function refused(): HTMLElement {
  const button = document.createElement("button");
  button.className = "shop-add shop-add-refused";
  button.textContent = "Add";
  button.disabled = true;
  return button;
}

function count(quantity: number): HTMLElement {
  const reading = document.createElement("span");
  reading.className = "shop-quantity";
  reading.textContent = String(quantity);
  return reading;
}
